import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import SectionHeading from './SectionHeading';
import Icon from './Icon';
import { companyLead, values } from '../content/company';

/**
 * Коротка довідка про компанію на головній.
 *
 * Історія цього місця: найдовший блок сторінки з візією й місією; потім
 * картка з абзацами й чотири картки цінностей; потім розворот із тезою
 * великим кеглем і рядком цінностей; потім фото з тезою заголовковим
 * шрифтом, абзацом і цінностями у два стовпці збоку. Остання редакція
 * зібрала в одній колонці три різні кеглі й два шрифти — власник назвав це
 * «хаотично», і мав рацію.
 *
 * Тепер два рівні й лише вони. Верхній — фото ліворуч і текст праворуч:
 * теза й абзац одним шрифтом (Inter), різниця між ними лише в кеглі та
 * кольорі. Нижній — чотири цінності одним рядком під обома колонками,
 * однакової будови: номер, назва, речення. Заголовковий шрифт живе тільки
 * в заголовках — так само, як у решті секцій.
 *
 * Фото — лікарка з пацієнткою (Pexels, знімок українського фотографа; див.
 * docs/photo-sources.md).
 *
 * На фото — речення з місії, а не цифри. Тут стояла смуга «01 спеціалізація ·
 * 06 напрямків · UA дистанційно»; власник спитав «чому так», і відповіді,
 * крім «щоб було», не знайшлося: цифра 01 нічого не рахує, а 06 повторює
 * лічильник у шапці «Наших послуг». Речення з місії каже те, заради чого
 * секція існує. Вигаданих «15 років» і «300 клієнтів» тут немає навмисно —
 * юридична фірма не ставить на сайт цифри, за які не відповідає.
 *
 * Пропорція кадру 4:3, а не 4:5, як було: усі фото сайту горизонтальні (16:10),
 * і у вертикальній рамці object-cover з'їдав половину кадру — від сцени
 * лишалася смуга посередині.
 */
const MISSION_LINE = 'Лікар має лікувати, а менеджер — розвивати бізнес. Складні юридичні процеси ми беремо на себе.';

const About = () => {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  return (
    <section id="about" className="py-16 lg:py-24 bg-white" ref={ref}>
      <div className="section-container">
        <motion.div
          className="mb-10 lg:mb-14"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <SectionHeading
            title="Про компанію"
            caption="Юристи з медичного права для клінік"
            badge={{ value: 'VMG', label: 'Visia Medical Group' }}
          />
        </motion.div>

        <div className="grid gap-10 lg:gap-16 lg:grid-cols-12 items-center">
          <motion.figure
            className="lg:col-span-5 order-last lg:order-first relative rounded-2xl overflow-hidden bg-slate-900 aspect-[4/3]"
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.15 }}
          >
            {/* alt порожній: фото декоративне, зміст несе текст поруч. */}
            <img
              src={process.env.PUBLIC_URL + '/images/about-clinic-51744b79-sm.jpg'}
              srcSet={`${process.env.PUBLIC_URL}/images/about-clinic-51744b79-sm.jpg 800w, ${process.env.PUBLIC_URL}/images/about-clinic-51744b79.jpg 1600w`}
              sizes="(min-width: 1024px) 40vw, 100vw"
              alt=""
              aria-hidden="true"
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink-deep/70 via-ink-deep/10 to-transparent"></div>

            <figcaption className="absolute inset-x-0 bottom-0 p-4 lg:p-5">
              <div className="rounded-xl bg-white/95 backdrop-blur-md shadow-sm p-4 lg:p-5">
                <div className="flex items-center gap-3 mb-2">
                  <span className="w-6 h-px bg-gradient-to-r from-teal-500 to-sky-400 flex-shrink-0"></span>
                  <span className="text-[0.65rem] uppercase tracking-[0.2em] text-teal-700 font-semibold">
                    Наша місія
                  </span>
                </div>
                <p className="text-sm lg:text-[15px] leading-snug font-medium text-slate-800">
                  {MISSION_LINE}
                </p>
              </div>
            </figcaption>
          </motion.figure>

          <motion.div
            className="lg:col-span-7"
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.25 }}
          >
            <p className="text-xl lg:text-2xl font-medium leading-relaxed text-slate-900">
              {companyLead[1]}
            </p>
            <p className="mt-6 text-base lg:text-lg leading-relaxed text-slate-600">
              {companyLead[0]}
            </p>
            <Link
              to="/pro-kompaniyu"
              className="mt-7 inline-flex items-center gap-2 text-teal-700 font-semibold hover:text-teal-800 transition-colors duration-200"
            >
              Візія, місія та порядок роботи
              <Icon name="fa-arrow-right" />
            </Link>
          </motion.div>
        </div>

        <motion.div
          className="mt-14 lg:mt-20 pt-10 border-t border-slate-200 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-8"
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          {values.map((value, index) => (
            <div key={value.title}>
              <div className="text-xs font-bold tracking-[0.2em] text-teal-700 mb-3">
                {String(index + 1).padStart(2, '0')}
              </div>
              <h3 className="font-display font-bold text-slate-900 mb-2">{value.title}</h3>
              <p className="text-sm leading-relaxed text-slate-600">{value.description}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default About;
